import { useFeatureFlag } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { PatientIvStatusButtonFactory } from 'apps/wrapper/src/components/insurance-verification/patient-flow/status-button/status-button-factory';
import { FC, useEffect, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { useInsuranceApi } from '../../../../../../hooks/use-insurance-api';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { PatientVerificationSummary } from '../../../../../../services/insurance/types/patient-verification-summary';
import { IInsuranceInfo, PatientInfo } from '../../../../../../services/api-client/contact-api';
import { EventIvStatusUpdated } from '../../../../../../types/event-bus';
import AccordionContent from '../../../../../shared/accordion/accordion-content';
import AccordionDetails from '../../../../../shared/accordion/accordion-details';
import AccordionSection from '../../../../../shared/accordion/accordion-section';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { CareCreditSection } from '../shared/sections/care-credit-section';
import { ViewContent } from '../shared/view-content/view-content';
import { BalanceSection } from './billing.styles';
import { getBalanceChip } from './utils/get-balance-chip';

export const BillingView: FC = () => {
  const { listen } = useEventBus();
  const { hasFlag } = useFeatureFlag();
  const { listen: listenNotification } = useNotifications();
  const { patientInfo, contactDetails, locationId } = usePatientProfile();
  const { status, getPatientVerificationSummaries, getPracticeOnboarding } = useInsuranceApi();
  const [isIv2Active, setIsIv2Active] = useState<boolean>(false);
  const [patientIv, setPatientIv] = useState<PatientVerificationSummary>();

  const primBenefitsRemaining = patientInfo.primBenefitsRemaining ? `$${patientInfo.primBenefitsRemaining}` : '-';
  const balance30 = patientInfo.outstandingBalance30Days ? patientInfo.outstandingBalance30Days : 0;
  const balance60 = patientInfo.outstandingBalance60Days ? patientInfo.outstandingBalance60Days : 0;
  const balance90 = patientInfo.outstandingBalance90Days ? patientInfo.outstandingBalance90Days : 0;

  useEffect(() => {
    (async () => {
      if (hasFlag('InsuranceVerification2')) {
        const onboarding = await getPracticeOnboarding(locationId);
        const isActive = onboarding?.status === 'Active' && (onboarding?.isInsuranceVerificationDisplayActive || false);
        if (isActive) loadInsuranceVerifications();
        setIsIv2Active(isActive);
      }
    })();
  }, [locationId]);

  useEffect(() => {
    return listen('ivStatusUpdated', (event) => {
      const { data } = event as EventIvStatusUpdated;

      patientIv && data.status === 'InsuranceInProcess'
        ? setPatientIv({ ...patientIv, pendingIvPriority: data.priority })
        : loadInsuranceVerifications();
    });
  }, [listen, patientIv, setPatientIv]);

  useEffect(() => {
    return listenNotification('insurance-verification-update', (event: any) => {
      const locId = event?.locationId || event?.locationid;

      if (locId === locationId) {
        loadInsuranceVerifications();
      }
    });
  }, [locationId, listenNotification]);

  const loadInsuranceVerifications = async () => {
    const patientVerifications = await getPatientVerificationSummaries([contactDetails.id], locationId);
    if (patientVerifications.length > 0) setPatientIv(patientVerifications[0]);
  };

  const getCarrierId = (insuranceInfo?: IInsuranceInfo) => insuranceInfo?.carrierId ?? '-';

  const getPrimaryInsuranceInfo = () => {
    if (isIv2Active && patientIv && patientInfo?.insuranceInfo?.carrierId) {
      return patientInfo?.insuranceInfo?.carrierId ?? '-';
    }
    return patientInfo?.insurance ?? '-';
  };

  const getSecondaryInsuranceInfo = () => patientInfo?.secondaryInsuranceInfo?.carrierId ?? '-';

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu
        loading={status.getPatientVerificationSummaries.isRunning || status.getPracticeOnboarding.isRunning}
      />
      <ViewContent>
        <AccordionDetails>
          <AccordionSection header="Primary Insurance">
            <AccordionContent>
              <label>{getPrimaryInsuranceInfo()}</label>
            </AccordionContent>
          </AccordionSection>
          {isIv2Active && patientIv && (patientInfo?.insuranceInfo?.carrierId || patientIv.primaryVerificationSummary) && (
            <AccordionSection header="Primary Insurance Status">
              <AccordionContent>
                <PatientIvStatusButtonFactory
                  patientId={contactDetails.id}
                  locationId={contactDetails.location?.id}
                  verificationPriority="Primary"
                  selectedSummary={patientIv?.primaryVerificationSummary}
                  patientSummary={patientIv}
                />
              </AccordionContent>
            </AccordionSection>
          )}
          <AccordionSection header="Secondary Insurance">
            <AccordionContent>
              <label>{getSecondaryInsuranceInfo()}</label>
            </AccordionContent>
          </AccordionSection>
          {isIv2Active && patientIv && (patientInfo?.secondaryInsuranceInfo?.carrierId || patientIv.secondaryVerificationSummary) && (
            <AccordionSection header="Secondary Insurance Status">
              <AccordionContent>

                <PatientIvStatusButtonFactory
                  patientId={contactDetails.id}
                  locationId={contactDetails.location?.id}
                  verificationPriority="Secondary"
                  selectedSummary={patientIv?.secondaryVerificationSummary}
                  patientSummary={patientIv}
                />

              </AccordionContent>
            </AccordionSection>
          )}
          <AccordionSection header="Primary Insurance Remaining">
            <AccordionContent>
              <label>{primBenefitsRemaining}</label>
            </AccordionContent>
          </AccordionSection>
          <AccordionSection header="Balances">
            <AccordionContent>
              <BalanceSection>
                {getBalanceChip(balance30)}
                <label>{`30 Day - $${balance30}`}</label>
              </BalanceSection>
              <BalanceSection>
                {getBalanceChip(balance60)}
                <label>{`60 Day - $${balance60}`}</label>
              </BalanceSection>
              <BalanceSection>
                {getBalanceChip(balance90)}
                <label>{`90 Day - $${balance90}`}</label>
              </BalanceSection>
            </AccordionContent>
          </AccordionSection>
          <CareCreditSection />
        </AccordionDetails>
      </ViewContent>
    </>
  );
};
